import { useCallback, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { AddressAutocompleteForm } from "components/AddressAutocompleteForm";
import {
  normalizeCountryToCode,
  normalizeRegionToCode,
} from "components/AddressAutocompleteForm/utils";
import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { useWriterComponentState } from "hooks/useWriterComponentState";

import { ClientAppEvent } from "../../../../constants";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: number;
  address: CompanyAddress;
}

const REQUIRED_ADDRESS_KEYS: (keyof CompanyAddress)[] = [
  "address",
  "city",
  "postal",
  "country",
];

export function EditCompanyAddressModal(props: Props) {
  const { open, onClose, onSuccess, companyId, address } = props;
  const posthog = usePostHog();

  const normalizedCountry = normalizeCountryToCode(address.country);
  const initialAddress = {
    address: address.address,
    city: address.city,
    province: normalizeRegionToCode(normalizedCountry, address.province),
    postal: address.postal,
    country: normalizedCountry,
  };
  const [addressData, setAddressData] = useState<Partial<CompanyAddress>>({
    ...initialAddress,
    type: address.type,
  });

  const normalizedPostal = addressData.postal?.toUpperCase().replace(/ /g, "");
  const { loading, error, write, resetState } = useWriterComponentState({
    writeConfig: {
      method: "patch",
      url: `/companies/${companyId}/address`,
      body: { ...addressData, postal: normalizedPostal },
    },
    errorMessage: "Failed to update address",
    errorMessageKey: "message",
  });

  const onAddressValueChanged = useCallback(
    (key: keyof CompanyAddress, value: string): void => {
      setAddressData((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const isAddressIncomplete =
    REQUIRED_ADDRESS_KEYS.some(
      (key) => (addressData[key] ?? "").length === 0,
    ) ||
    ((addressData.country === "CA" || addressData.country === "US") &&
      !addressData.province);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSubmit = () => {
    write(() => {
      posthog?.capture(ClientAppEvent.COMPANY_ADDRESS_UPDATED);
      onSuccess();
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h5">Edit Company Address</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          <AddressAutocompleteForm
            onValueChanged={onAddressValueChanged}
            initialValues={initialAddress}
          />
          <ErrorTypography error={error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingWrappedNode loading={loading}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isAddressIncomplete || loading}
          >
            Save Address
          </Button>
        </LoadingWrappedNode>
      </DialogActions>
    </Dialog>
  );
}
